/**
 * What "Feedback for This Page" fills in by itself — the parts of a support
 * case the page already knows. Kept apart from the dialog so the rules are
 * plain functions: which system a path is about, who a case is preset to,
 * and the rows the description carries below the person's words.
 */

import type { PageOwners } from "@/hooks/use-page-owners";
import { CATEGORY_META, SYSTEM_META } from "@/lib/tickets";
import type { CaseCategory, CaseSystem, UserRef } from "@/services/tickets-services";

/** A case filed from the Kory page is about Kory in the browser; from anywhere
 *  else in the app it is HickoryOS. */
export function systemForPath(pathname: string): CaseSystem {
  return pathname === "/kory" || pathname.startsWith("/kory/") ? "kory_browser" : "hickoryos";
}

/** Who a page-feedback case is preset to: the technical owner for a bug (it is
 *  theirs to fix), the page owner for a question or a feature request (it is
 *  theirs to answer, or to want) — or the technical owner when there is no page
 *  owner the caller could assign, so the case still reaches someone. Null when
 *  the page has no such owner — the backend only hands back owners it would
 *  accept. */
export function presetAssignee(category: CaseCategory | null, owners: PageOwners | undefined): UserRef | null {
  if (!category || !owners) return null;
  if (category === "bug") return owners.technical_owner_user;
  return owners.page_owner_user ?? owners.technical_owner_user;
}

export interface PageFeedbackContext {
  system: CaseSystem;
  category: CaseCategory;
  /** Path + query of the page the case is about, e.g. ``/overview?tab=kpis``. */
  pagePath: string;
  owners: PageOwners | undefined;
  /** Who the case is being assigned to, so the owner rows can say so. */
  assigneeId: string | null;
}

/** The person's words first, then the rows every page-feedback case carries,
 *  in the shape Kory's cases use — so the sheet shows what happened before the
 *  labels, and the routing agent and every outbound body see the page. */
export function pageFeedbackDescription(details: string, ctx: PageFeedbackContext): string {
  const rows = [
    `**System:** ${SYSTEM_META[ctx.system].label}`,
    `**Category:** ${CATEGORY_META[ctx.category].label}`,
    `**Page:** ${ctx.pagePath}`,
  ];
  const ownerRow = (label: string, name: string | null | undefined, user: UserRef | null | undefined) => {
    if (!name) return;
    const assigned = user != null && user.id === ctx.assigneeId ? " (assigned)" : "";
    rows.push(`**${label}:** ${name}${assigned}`);
  };
  ownerRow("Technical owner", ctx.owners?.technical_owner_name, ctx.owners?.technical_owner_user);
  ownerRow("Page owner", ctx.owners?.page_owner_name, ctx.owners?.page_owner_user);
  const words = details.trim();
  return `${words ? `${words}\n\n---\n\n` : ""}${rows.join("\n")}\n\n*Submitted via Feedback for This Page*`;
}
